<template>
  <div>
    <v-simple-table v-if="options.length > 0">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">名稱</th>
            <th class="text-center" width="100">排序</th>
            <th class="text-center" width="150">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="option in options" :key="option._id">
            <td>{{ option.name }}</td>
            <td class="text-center">{{ option.order }}</td>
            <td class="text-center">
              <v-btn icon small @click="editOption(option)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small color="error" @click="confirmDelete(option)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-alert v-else type="info" outlined dense class="mt-2"> 暫無數據 </v-alert>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500px">
      <v-card>
        <v-card-title>編輯選項</v-card-title>
        <v-card-text>
          <ValidationObserver ref="form" v-slot="{ invalid }">
            <ValidationProvider v-slot="{ errors }" name="名稱" rules="required">
              <v-text-field
                v-model="editForm.name"
                :error="!!errors.length"
                :error-messages="errors"
                label="名稱 *"
                outlined
                dense
              />
            </ValidationProvider>
            <ValidationProvider v-slot="{ errors }" name="排序" rules="numeric">
              <v-text-field
                v-model.number="editForm.order"
                :error="!!errors.length"
                :error-messages="errors"
                label="排序"
                outlined
                dense
                type="number"
                hint="數字越小越靠前"
              />
            </ValidationProvider>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeEditDialog">取消</v-btn>
          <v-btn color="primary" @click="saveOption">儲存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getOptions, updateOption, deleteOption } from '@/api/options';
import api from '@/api';
import { mapActions } from 'vuex';

export default {
  name: 'OptionsList',
  props: {
    type: {
      type: String,
      required: true,
    },
    subType: {
      type: String,
      default: '',
    },
    initialOptions: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      options: [],
      editDialog: false,
      editingOption: null,
      editForm: {
        name: '',
        order: 0,
      },
    };
  },
  watch: {
    initialOptions: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.options = newVal;
        }
      },
    },
  },
  mounted() {
    // Only load if initialOptions not provided
    if (!this.initialOptions) {
      this.loadOptions();
    }
    this.$root.$on('reload-options', this.loadOptions);
  },
  beforeDestroy() {
    this.$root.$off('reload-options', this.loadOptions);
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),
    async loadOptions() {
      try {
        const filter = { type: this.type };
        // Always filter by subType (including empty string)
        filter.subType = this.subType;
        const data = await getOptions(api, {
          filter,
          sort: { order: 1, createdAt: -1 },
        });
        this.options = data.docs || data;
      } catch (e) {
        console.error('Failed to load options:', e);
      }
    },
    editOption(option) {
      this.editingOption = option;
      this.editForm = {
        name: option.name,
        order: option.order,
      };
      this.editDialog = true;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.editingOption = null;
      this.$refs.form?.reset();
    },
    async saveOption() {
      try {
        if (!(await this.$refs.form?.validate?.())) {
          return;
        }

        await updateOption(api, this.editingOption._id, this.editForm);
        await this.updateAlertMessage({
          msg: '更新成功',
          type: 'success',
          color: 'success',
        });

        this.closeEditDialog();
        this.loadOptions();
      } catch (e) {
        await this.updateAlertMessage({
          msg: e.message || '更新失敗',
          type: 'error',
          color: 'error',
        });
      }
    },
    async confirmDelete(option) {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此記錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
      );
      if (!confirmed) {
        return;
      }
      await this.deleteOption(option);
    },
    async deleteOption(option) {
      try {
        await deleteOption(api, option._id);
        await this.updateAlertMessage({
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });
        this.loadOptions();
      } catch (e) {
        await this.updateAlertMessage({
          msg: e.message || '刪除失敗',
          type: 'error',
          color: 'error',
        });
      }
    },
  },
};
</script>
